import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { formatAmount, toCamelCase } from "../../utils";
import { getPrivateStock } from "../../redux/stocks/stockSlice";
import {
  addListingData,
  deleteListingData,
} from "../../redux/admin/adminSlice";
import toast from "react-hot-toast";
import AppLoader from "../../components/AppLoader/AppLoader";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import {
  AddListingButton,
  bodyCellSx,
  DeleteAction,
  EmptyTableRow,
  ListingAvatar,
  ListingPageShell,
  headCellSx,
  primaryButtonSx,
  SortableHeader,
} from "../../components/AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT } from "../../constants/layout";

const PrivateStockTableRow = ({
  data,
  onDelete,
}: {
  data: any;
  onDelete: (id: number) => void;
}) => {
  const userType = Number(localStorage.getItem("userType")) || 0;
  const isValidAssetName = (assetName: string) =>
    assetName && assetName !== "-" && assetName.trim() !== "";

  if (!data?.stockName || !isValidAssetName(data.stockName)) return null;

  return (
    <TableRow hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
      <TableCell sx={bodyCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ListingAvatar name={data.stockName} />
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: DARK_NAVY }}>
            {toCamelCase(data.stockName)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
        {data.stockSector || "-"}
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
        {data.lotSize ? formatAmount(data.lotSize) : "-"}
      </TableCell>
      {userType === 4 && (
        <TableCell sx={bodyCellSx} align="center">
          <DeleteAction onClick={() => onDelete(data.id)} />
        </TableCell>
      )}
    </TableRow>
  );
};

const PrivateStockListing = () => {
  const dispatch = useDispatch<any>();
  const userType = Number(localStorage.getItem("userType")) || 0;
  const [loader, setLoader] = React.useState(true);
  const [totalEnteries, setTotalEnteries] = useState<number>();
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [stockData, setStockData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [formData, setFormData] = useState({
    stockName: "",
    sector: "",
    currentPrice: "",
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string | null;
  }>({
    key: "assetName",
    direction: "ascending",
  });
  //Fetch MF Data from Get All fund list API
  const fetchData = async () => {
    setLoader(true); // Start loading
    try {
      const response = await dispatch(
        getPrivateStock({ pageIndex: 1, pageSize: 1000 })
      );

      if (response) {
        let data = response?.payload?.data?.data?.pmsList || [];
        setTotalEnteries(response?.payload?.data?.data?.pmsList?.length);
        setStockData(data);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoader(false); // End loading
      //   setfilterButton(false);
    }
  };

  useEffect(() => {
    setLoader(true);
    fetchData();
  }, []);

  useEffect(() => {
    if (searchInput === "") {
      setFilteredData(stockData);
    } else {
      const lowercasedInput = searchInput.toLowerCase();
      const newFilteredData = stockData.filter((entry: any) =>
        entry.stockName.toLowerCase()?.includes(lowercasedInput)
      );
      setFilteredData(newFilteredData);
    }
  }, [searchInput, stockData]);

  const getSortedData = useCallback(() => {
    if (!sortConfig.key) return filteredData;

    const sortedData = [...filteredData];

    switch (sortConfig.key) {
      case "stockName":
        sortedData.sort((a, b) => {
          const nameA = (a.stockName || "").toUpperCase();
          const nameB = (b.stockName || "").toUpperCase();
          return (
            (nameA < nameB ? -1 : nameA > nameB ? 1 : 0) *
            (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      case "stockSector":
        sortedData.sort((a, b) => {
          const nameA = (a.stockSector || "").toUpperCase();
          const nameB = (b.stockSector || "").toUpperCase();
          return (
            (nameA < nameB ? -1 : nameA > nameB ? 1 : 0) *
            (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      case "lotSize":
        sortedData.sort((a, b) => {
          const lotA = a.lotSize || 0;
          const lotB = b.lotSize || 0;
          return (
            (lotA - lotB) * (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      default:
        break;
    }

    return sortedData;
  }, [filteredData, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const sortedData = getSortedData();

  const handleAddStock = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        addListingData({
          listingType: 8,
          ...formData,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding stock.");
        return;
      }

      toast.success("Stock added successfully.");
      setShowAddStockModal(false);
      setFormData({
        stockName: "",
        sector: "",
        currentPrice: "",
      });
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const onDelete = (id: number) => {
    setShowDeleteModal(true);
    setDeleteId(id);
  };

  const handleDeleteStock = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        deleteListingData({
          listingType: 8,
          id: deleteId,
        })
      );
      if (response?.error?.message) {
        toast.error("Error deleting stock.");
        return;
      }

      toast.success("Stock deleted successfully.");
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <AppLoader loading={loader} />
      <ListingPageShell
        title="Unlisted Equity"
        subtitle="Browse and manage unlisted equity stocks available on the platform."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search unlisted equity by stock name..."
        action={
          userType === 4 ? (
            <AddListingButton label="Add Unlisted Equity" onClick={() => setShowAddStockModal(true)} />
          ) : undefined
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Equity Name" onSort={() => handleSort("stockName")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Depository Applicable" onSort={() => handleSort("stockSector")} />
                </TableCell>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Lot Size" onSort={() => handleSort("lotSize")} />
                </TableCell>
                {userType === 4 && (
                  <TableCell sx={{ ...headCellSx, width: 88 }} align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <EmptyTableRow colSpan={userType === 4 ? 4 : 3} />
                </TableRow>
              ) : (
                sortedData.map((row: any, index: number) => (
                  <PrivateStockTableRow key={row.id ?? index} data={row} onDelete={onDelete} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ListingPageShell>
      <Modal
          open={showAddStockModal}
          onClose={() => setShowAddStockModal(false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 800, // Adjust width as needed
              maxHeight: "80vh", // Limit the height of the modal
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              overflowY: "auto", // Make the content scrollable on the Y-axis
              borderRadius: 2,
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6" component="h2">
                Add Unlisted Equity
              </Typography>
              <IconButton onClick={() => setShowAddStockModal(false)}>
                <Close />
              </IconButton>
            </Box>
            <Box component="form" noValidate autoComplete="off">
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label="Stock Name"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="stockName"
                  type="text"
                  onChange={(e) =>
                    setFormData({ ...formData, stockName: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  value={formData?.stockName || ""}
                />
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel id="stock-depository-label">
                    Stock Depository
                  </InputLabel>
                  <Select
                    labelId="stock-depository-label"
                    onChange={(e) =>
                      setFormData({ ...formData, sector: e.target.value })
                    }
                    label="Stock Depository"
                    value={formData?.sector || ""}
                  >
                    <MenuItem value="NSDL">NSDL</MenuItem>
                    <MenuItem value="CDSL">CDSL</MenuItem>
                    <MenuItem value="NSDL & CDSL">NSDL & CDSL</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Current Price"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="currentPrice"
                  type="number"
                  onChange={(e) =>
                    setFormData({ ...formData, currentPrice: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  value={formData?.currentPrice || ""}
                />
              </div>
              <div className="mt-4">
                <Button
                  onClick={handleAddStock}
                  sx={primaryButtonSx}
                  disabled={
                    !formData.stockName ||
                    !formData.sector ||
                    !formData.currentPrice
                  }
                >
                  Add
                </Button>
              </div>
            </Box>
          </Box>
        </Modal>
        <ConfirmDialog
          title="Delete Unlisted Stock"
          message="Are you sure you want to delete this stock?"
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
          onDelete={handleDeleteStock}
        />
    </>
  );
};

export default PrivateStockListing;
